import axios from "axios";
import {
	HelpAssets,
	HelpDefaultFaq,
	HelpFaqInput,
	HelpImportResult,
	HelpPresenceEditor,
	HelpSectionChanges,
	HelpSectionInput,
	HelpTopic,
	HelpTopicChanges,
	HelpTopicInput,
	HelpTopicsResponse,
	HelpUploadedImage,
} from "../../types/helpCentre.types";
import { url, AuthHeader } from "../../utils/_exports";
import {
	HELP_ASSET_PREFIX,
	helpAssetIdOf,
	isRecord,
	isText,
	records,
	toReaderFaqs,
} from "../../utils/helpCentre/content";

const contentUrl = (path: string) => `${url}/help-centre${path}`;

const topicUrl = (topicId: string) =>
	contentUrl(`/topics/${encodeURIComponent(topicId)}`);

const sectionUrl = (topicId: string, sectionId: string) =>
	`${topicUrl(topicId)}/sections/${encodeURIComponent(sectionId)}`;

const unexpected = (path: string) =>
	new TypeError(`Unexpected response from help-centre${path}`);

/**
 * The Help Centre topics, in the server's order. Super-admins are sent hidden
 * content too (`canEdit`). An API path that isn't mounted answers with the
 * app's HTML page and a 200, so anything but the expected object is an error.
 */
export const getHelpTopics = async (): Promise<HelpTopicsResponse> => {
	const { data } = await axios.get<unknown>(contentUrl("/topics"), AuthHeader);
	if (!isRecord(data) || !Array.isArray(data.topics)) {
		throw unexpected("/topics");
	}
	return { topics: data.topics, canEdit: data.canEdit === true };
};

/** The default FAQs in a response; editors are also sent the `version` to save with. */
const toDefaultFaq = (value: unknown, path: string): HelpDefaultFaq => {
	if (!isRecord(value)) throw unexpected(path);
	return {
		title: typeof value.title === "string" ? value.title : "",
		faq: toReaderFaqs(value.faq),
		...(typeof value.version === "number" ? { version: value.version } : {}),
	};
};

/** The default FAQs shown on the Help Centre's landing page. */
export const getHelpDefaultFaq = async (): Promise<HelpDefaultFaq> => {
	const { data } = await axios.get<unknown>(contentUrl("/faqs"), AuthHeader);
	return toDefaultFaq(isRecord(data) ? data.defaultFaq : undefined, "/faqs");
};

/** The topic a write answers with. */
const topicIn = (data: unknown, path: string): HelpTopic => {
	const topic = isRecord(data) ? data.topic : undefined;
	if (!isRecord(topic) || typeof topic._id !== "string") {
		throw unexpected(path);
	}
	return topic as HelpTopic;
};

/*
 * Writes, for editors only (the server refuses anyone else). Each topic write
 * names the version it was based on; the server refuses a stale one with 409.
 */

export const createHelpTopic = async (
	input: HelpTopicInput
): Promise<HelpTopic> => {
	const { data } = await axios.post<unknown>(
		contentUrl("/topics"),
		input,
		AuthHeader
	);
	return topicIn(data, "/topics");
};

export const updateHelpTopic = async (
	topicId: string,
	version: number,
	changes: HelpTopicChanges
): Promise<HelpTopic> => {
	const { data } = await axios.patch<unknown>(
		topicUrl(topicId),
		{ version, ...changes },
		AuthHeader
	);
	return topicIn(data, "/topics/:id");
};

export const deleteHelpTopic = async (
	topicId: string,
	version: number
): Promise<void> => {
	const { data } = await axios.delete<unknown>(topicUrl(topicId), {
		...AuthHeader,
		params: { version },
	});
	if (!isRecord(data) || typeof data.id !== "string") {
		throw unexpected("/topics/:id");
	}
};

/** Put every topic in the order of `ids`; answers with all topics. */
export const reorderHelpTopics = async (
	ids: string[]
): Promise<HelpTopic[]> => {
	const { data } = await axios.put<unknown>(
		contentUrl("/topics/order"),
		{ ids },
		AuthHeader
	);
	if (!isRecord(data) || !Array.isArray(data.topics)) {
		throw unexpected("/topics/order");
	}
	return data.topics;
};

export const createHelpSection = async (
	topicId: string,
	version: number,
	input: HelpSectionInput
): Promise<HelpTopic> => {
	const { data } = await axios.post<unknown>(
		`${topicUrl(topicId)}/sections`,
		{ version, ...input },
		AuthHeader
	);
	return topicIn(data, "/topics/:id/sections");
};

export const updateHelpSection = async (
	topicId: string,
	sectionId: string,
	version: number,
	changes: HelpSectionChanges
): Promise<HelpTopic> => {
	const { data } = await axios.patch<unknown>(
		sectionUrl(topicId, sectionId),
		{ version, ...changes },
		AuthHeader
	);
	return topicIn(data, "/topics/:id/sections/:sectionId");
};

export const deleteHelpSection = async (
	topicId: string,
	sectionId: string,
	version: number
): Promise<HelpTopic> => {
	const { data } = await axios.delete<unknown>(sectionUrl(topicId, sectionId), {
		...AuthHeader,
		params: { version },
	});
	return topicIn(data, "/topics/:id/sections/:sectionId");
};

/** Put every section of the topic in the order of `ids`. */
export const reorderHelpSections = async (
	topicId: string,
	version: number,
	ids: string[]
): Promise<HelpTopic> => {
	const { data } = await axios.put<unknown>(
		`${topicUrl(topicId)}/sections/order`,
		{ version, ids },
		AuthHeader
	);
	return topicIn(data, "/topics/:id/sections/order");
};

/** Save the default FAQs; version 0 creates them. */
export const saveHelpDefaultFaq = async (
	version: number,
	title: string,
	faq: HelpFaqInput[]
): Promise<HelpDefaultFaq> => {
	const { data } = await axios.put<unknown>(
		contentUrl("/faqs"),
		{ version, title, faq },
		AuthHeader
	);
	return toDefaultFaq(isRecord(data) ? data.defaultFaq : undefined, "/faqs");
};

/** Every topic and the default FAQs, as the export file the import takes back. */
export const exportHelpContent = async (): Promise<Record<string, unknown>> => {
	const { data } = await axios.get<unknown>(contentUrl("/export"), AuthHeader);
	if (!isRecord(data) || data.format !== "terra-help-content") {
		throw unexpected("/export");
	}
	return data;
};

/**
 * Check an export file against the current content and say what importing it
 * would do. Only with `apply` does it replace everything.
 */
export const importHelpContent = async (
	content: unknown,
	apply: boolean
): Promise<HelpImportResult> => {
	const { data } = await axios.post<unknown>(
		contentUrl("/import"),
		{ content, apply },
		AuthHeader
	);
	const summary = isRecord(data) ? data.summary : undefined;
	if (
		!isRecord(data) ||
		typeof data.applied !== "boolean" ||
		!isRecord(summary) ||
		!isRecord(summary.topics) ||
		!Array.isArray(summary.topics.added) ||
		!Array.isArray(summary.topics.removed) ||
		!Array.isArray(summary.topics.kept) ||
		!isRecord(summary.sections) ||
		!isRecord(summary.defaultFaqs)
	) {
		throw unexpected("/import");
	}
	return data as unknown as HelpImportResult;
};

/**
 * Links to the images in what this user can see, by id: signed by the server
 * for an hour or two, read-only. Only `https:` links are kept.
 */
export const getHelpAssets = async (): Promise<HelpAssets> => {
	const { data } = await axios.get<unknown>(contentUrl("/assets"), AuthHeader);
	const assets = isRecord(data) ? data.assets : undefined;
	if (!isRecord(assets)) throw unexpected("/assets");
	return Object.fromEntries(
		Object.entries(assets).filter(
			(entry): entry is [string, string] =>
				isText(entry[1]) && entry[1].startsWith("https://")
		)
	);
};

/** Upload an image for an article, for editors only. The server reads its type from its bytes. */
export const uploadHelpImage = async (
	file: File
): Promise<HelpUploadedImage> => {
	const form = new FormData();
	form.append("image", file, file.name);
	const { data } = await axios.post<unknown>(
		contentUrl("/assets"),
		form,
		AuthHeader
	);
	const image = isRecord(data) ? data.image : undefined;
	if (
		!isRecord(image) ||
		!isText(image.id) ||
		!helpAssetIdOf(`${HELP_ASSET_PREFIX}${image.id}`) ||
		!isText(image.url) ||
		!image.url.startsWith("https://")
	) {
		throw unexpected("/assets");
	}
	return { id: image.id, url: image.url };
};

/**
 * Say this tab has `subject` (a topic's id, or `default-faq`) open in an
 * editor; answers with who else has it open. For editors only.
 */
export const sendHelpPresence = async (
	tab: string,
	subject: string,
	signal?: AbortSignal
): Promise<HelpPresenceEditor[]> => {
	const { data } = await axios.put<unknown>(
		contentUrl("/presence"),
		{ tab, subject },
		{ ...AuthHeader, signal }
	);
	if (!isRecord(data) || !Array.isArray(data.editors)) {
		throw unexpected("/presence");
	}
	return records(data.editors)
		.filter((editor) => isText(editor.name) && editor.name)
		.map((editor) => ({ name: editor.name, you: editor.you === true }));
};

/** Say this tab has closed its editor. */
export const leaveHelpPresence = async (tab: string): Promise<void> => {
	await axios.delete(contentUrl("/presence"), {
		...AuthHeader,
		params: { tab },
	});
};

export interface HelpRequestInput {
	subject: string;
	message: string;
	consent: boolean;
	phone?: string;
	contactMethod?: "email" | "phone";
	files?: File[];
}

export interface HelpRequestResult {
	reference: string;
	confirmationSent: boolean;
}

/**
 * Send a Help Centre support request. The server takes the sender's name and
 * email from the signed-in session, so only what the user typed is sent. The
 * tenant goes in the `tenantKey` header and is checked against their roles.
 */
export const submitHelpRequest = (
	input: HelpRequestInput,
	tenantKey: string
): Promise<HelpRequestResult> => {
	const form = new FormData();
	form.append("subject", input.subject);
	form.append("message", input.message);
	form.append("consent", String(input.consent));
	if (input.phone) form.append("phone", input.phone);
	if (input.contactMethod) form.append("contact_method", input.contactMethod);
	(input.files ?? []).forEach((file) =>
		form.append("attachments", file, file.name)
	);

	return axios
		.post(`${url}/help-centre/requests`, form, {
			...AuthHeader,
			headers: { ...AuthHeader.headers, tenantKey },
		})
		.then((res) => {
			// Never report success without the reference the server issues.
			if (typeof res.data?.reference !== "string") {
				throw new TypeError("The Help Centre gave no request reference.");
			}
			return res.data;
		});
};
